<template>
  <div>
    <!-- Show message when no occurrences found -->
    <div v-if="occurrencesData.length === 0" class="no-occurrences-message">
      <i class="pi pi-info-circle"></i>
      <p>{{ t('Nenhuma observação encontrada') }}</p>
    </div>

    <!-- Show table when occurrences exist -->
    <data-table
      v-else
      display-mode="handle_user_device"
      ref="dataTableRef"
      :config="dataTableConfig"
      :data="occurrencesData"
      @row-click="(e: any) => handleRowClick(e)"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useOcorrencias } from '../../composables/useOcorrencias';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useI18n } from 'vue-i18n';

type OwnerId = string | number | null | undefined;
type AllowDelete = string | boolean;

interface CellRendererParams {
  node?: { rowPinned?: boolean | string | null };
  value?: unknown;
}

interface ButtonClickPayload {
  value?: number | string;
  data?: Record<string, unknown>;
  action?: string;
}

interface OcorrenciaRow {
  ID?: number | string;
  CreatedAt?: string;
  User?: string;
  Content?: string;
  [key: string]: unknown;
}

const { t } = useI18n();

const props = withDefaults(defineProps<{
  owner: string;
  ownerId: OwnerId;
  allowDelete?: AllowDelete;
  datatableLanguageUrl?: string;
}>(), {
  allowDelete: true,
  datatableLanguageUrl: ''
});

const emit = defineEmits<{
  'row-click': [rowData: OcorrenciaRow];
  'delete-occurrence': [id: number | string];
  'button-click': [payload: ButtonClickPayload];
}>();

const { fetchOcorrencias, deleteOcorrencia } = useOcorrencias();
const { confirmAction } = useConfirmAction();

const occurrencesData = ref<OcorrenciaRow[]>([]);
const tableId = ref<string>(`tabelaOcorrencias_${Math.random().toString(36).substr(2, 9)}`);
const dataTableRef = ref<unknown>(null);

const dataTableConfig = computed(() => {
  const config = {
    tableId: tableId.value,
    pageLength: 100,
    pagination: false,
    showFilters: false,
    sortable: false,
    rowTestId: (data: Record<string, unknown>): string => {
      const id = data?.ID;
      return id == null ? '' : `occurrences-row-${id}`;
    },
    columns: [
      {
        data: "CreatedAt",
        title: "Data",
        width: 180,
        minWidth: 120,
        renderType: "datetime",
        dataType: "date"
      },
      {
        data: "User",
        title: "Usuário",
        width: 150,
        minWidth: 80
      },
      {
        data: "Content",
        title: "Conteúdo",
        minWidth: 200,
        cellRenderer: (params: CellRendererParams): string => {
          if (params.node && params.node.rowPinned) {
            return params.value != null ? String(params.value) : '';
          }
          return (params.value as string) || '';
        }
      },
      {
        data: "ID",
        title: "",
        width: 80,
        minWidth: 50,
        sortable: false,
        renderType: "clickable_button",
        buttonClass: "p-button p-button-sm p-button-danger p-button-text",
        buttonIcon: "pi pi-trash",
        buttonText: t('Excluir'),
        buttonCondition: (): boolean => Boolean(props.allowDelete) && (props.allowDelete === true || props.allowDelete === 'true')
      }
    ]
  };

  log('OccurrencesTable', 'dataTableConfig', 'Generated DataTable config', config);
  return config;
});

const loadData = async (): Promise<void> => {
  try {
    log('OccurrencesTable', 'loadData', 'Loading occurrences data');
    const response = await fetchOcorrencias({
      owner: props.owner,
      ownerID: Number(props.ownerId)
    }) as OcorrenciaRow[] | null | undefined;

    occurrencesData.value = [...(response || [])];

    log('OccurrencesTable', 'loadData', 'Loaded occurrences data', { count: occurrencesData.value.length, data: occurrencesData.value });
  } catch (error) {
    log('OccurrencesTable', 'loadData', 'Error', error);
  }
};

const handleRowClick = (rowData: OcorrenciaRow): void => {
  log('OccurrencesTable', 'handleRowClick', 'Occurrence row clicked', rowData);
  emit('row-click', rowData);
};

const handleButtonClick = (buttonData: ButtonClickPayload): void => {
  log('OccurrencesTable', 'handleButtonClick', 'Button clicked', buttonData);
  if (!buttonData.value) return;

  // O id da observação é um UUID (texto). Convertido para número virava NaN, o serviço não
  // achava nada para apagar e a lista recarregava igual, sem aviso — chamado 2026-0064.
  const id = String(buttonData.value);

  confirmAction({
    message: t('Tem certeza que deseja excluir esta observação?'),
    header: t('Confirmação'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: () => { void deletarOcorrencia(id); },
  });
};

const deletarOcorrencia = async (id: string): Promise<void> => {
  try {
    log('OccurrencesTable', 'deletarOcorrencia', 'Deleting occurrence', id);
    await deleteOcorrencia(id);
    log('OccurrencesTable', 'deletarOcorrencia', 'Occurrence deleted, reloading data');
    await loadData();
    log('OccurrencesTable', 'deletarOcorrencia', 'Data reloaded', occurrencesData.value);

    // Quem diz se apagou é a lista recarregada, não a resposta do serviço (um booleano
    // opaco): se a observação continua lá, não foi excluída, e quem clicou fica sabendo.
    if (occurrencesData.value.some((o) => String(o.ID) === id)) {
      throw new Error(t('a observação continua na lista'));
    }

    eventBus.emit('globalMessage.success', t('Observação excluída com sucesso'));
    emit('delete-occurrence', id);
  } catch (error) {
    // Falha em silêncio era o que fazia o botão parecer morto: quem clica precisa saber.
    log('OccurrencesTable', 'deletarOcorrencia', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao excluir observação: ') + message);
  }
};

const clearData = (): void => {
  occurrencesData.value = [];
};

watch(() => props.ownerId, (newVal) => {
  if (newVal && newVal !== "0" && newVal !== 0) {
    loadData();
  }
}, { immediate: true });

onMounted(() => {
  if (props.ownerId && props.ownerId !== "0" && props.ownerId !== 0) {
    loadData();
  }
});

onBeforeUnmount(() => {
  clearData();
});

defineExpose({
  loadData,
  clearData
});
</script>

<style scoped>
/* Empty state message */
.no-occurrences-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  text-align: center;
  color: #6c757d;
}

.no-occurrences-message i {
  font-size: 48px;
  margin-bottom: 16px;
  color: #adb5bd;
}

.no-occurrences-message p {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}
</style>
